import { FC, useState } from 'react'
import { infer as Infer, object, string } from 'zod'
import { useForm, FormProvider } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { Grid } from '@mui/material'

import {
  AppDialog,
  FormInputAutosuggest,
  FormInputTextField,
  Loader,
} from '../../../../components'
import { useAppDispatch } from '../../../../hooks'
import { showAlertAction } from '../../../../store/alerts'
import { fetchSlipsListAction } from '../../../../store/slips'
import { cancelEInvoice } from './Services'

const cancelReasons = [
  { id: '1', label: 'Duplicate' },
  { id: '2', label: 'Data Entry mistake' },
  { id: '3', label: 'Order Cancelled' },
  { id: '4', label: 'Others' },
]

const schema = object({
  cancelReason: string({ required_error: 'Please select a cancel reason' }),
  cancelRemark: string().min(1, 'Please enter a remark'),
})

type Schema = Infer<typeof schema>

export interface CancelEInvoiceProps {
  irn: string
  accountId: number
  onClose: () => void
}

export const CancelEInvoice: FC<CancelEInvoiceProps> = ({
  irn,
  accountId,
  onClose,
}) => {
  const dispatchAction = useAppDispatch()
  const [submitting, setSubmitting] = useState(false)

  const form = useForm<Schema>({
    mode: 'all',
    resolver: zodResolver(schema),
  })

  const { control, handleSubmit } = form

  const submitHandle = handleSubmit((data: Schema) => {
    setSubmitting(true)
    cancelEInvoice({
      Irn: irn,
      CnlRsn: data.cancelReason,
      CnlRem: data.cancelRemark,
    })
      .then((response) => {
        if (response.data?.success) {
          dispatchAction(
            showAlertAction({
              message: 'E-Invoice cancelled successfully',
              severity: 'success',
            })
          )
          dispatchAction(fetchSlipsListAction(accountId))
          const pdfUrl = response.data?.data
          if (pdfUrl) window.open(pdfUrl, '_blank')
          onClose()
        } else {
          dispatchAction(
            showAlertAction({
              message: response.data?.message ?? 'Failed to cancel E-Invoice',
              severity: 'error',
            })
          )
        }
      })
      .finally(() => setSubmitting(false))
  })

  return (
    <AppDialog
      id="cancel-einvoice"
      title="Cancel E-Invoice"
      submitButtonText="Cancel Invoice"
      onClose={onClose}
      onSubmit={submitHandle}
      close={false}
      maxWidth="sm"
    >
      {submitting && <Loader />}
      <FormProvider {...form}>
        <form>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <FormInputAutosuggest
                control={control}
                name="cancelReason"
                label="Cancel Reason"
                options={cancelReasons}
              />
            </Grid>
            <Grid item xs={12}>
              <FormInputTextField
                control={control}
                name="cancelRemark"
                label="Remark"
                required
              />
            </Grid>
          </Grid>
        </form>
      </FormProvider>
    </AppDialog>
  )
}
